import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Lightning, Flame, Eye, EyeSlash } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("worker");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  // Already logged in? Redirect
  if (user && user.id) {
    navigate("/");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = isRegister
      ? await register(name, email, password, role)
      : await login(email, password);
    if (result.success) {
      navigate("/");
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Lightning size={28} className="text-[#002FA7]" weight="fill" />
            <Flame size={28} className="text-[#FF5A00]" weight="fill" />
          </div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
            WorkPro
          </h1>
          <p className="text-xs text-[#404040] mt-1 font-mono-data">FIELD WORKER INTERFACE</p>
        </div>

        {/* Form */}
        <div className="wp-card">
          <h2 className="text-base font-bold mb-4" style={{ fontFamily: "'Chivo', sans-serif" }}>
            {isRegister ? "Create Account" : "Sign In"}
          </h2>

          {error && (
            <div className="p-2 mb-3 bg-[#FF5A0010] border border-[#FF5A00] rounded-sm text-xs text-[#FF5A00]" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {isRegister && (
              <div>
                <label className="text-[10px] font-mono-data text-[#404040] block mb-1">FULL NAME</label>
                <input
                  data-testid="auth-name-input"
                  type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-3 py-2.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
                  required
                />
              </div>
            )}

            <div>
              <label className="text-[10px] font-mono-data text-[#404040] block mb-1">EMAIL</label>
              <input
                data-testid="auth-email-input"
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full px-3 py-2.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
                required
              />
            </div>

            <div>
              <label className="text-[10px] font-mono-data text-[#404040] block mb-1">PASSWORD</label>
              <div className="relative">
                <input
                  data-testid="auth-password-input"
                  type={showPass ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="w-full px-3 py-2.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] pr-10"
                  required minLength={6}
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#404040]">
                  {showPass ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {isRegister && (
              <div>
                <label className="text-[10px] font-mono-data text-[#404040] block mb-1">ROLE</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "worker", label: "Field Worker", desc: "Full access to tools" },
                    { value: "customer", label: "Customer", desc: "View job progress" },
                  ].map(r => (
                    <button key={r.value} type="button"
                      data-testid={`role-${r.value}`}
                      onClick={() => setRole(r.value)}
                      className={`p-2 rounded-sm border text-left transition-colors ${
                        role === r.value ? "border-[#002FA7] bg-[#002FA708]" : "border-[#E5E5E5] hover:border-[#0A0A0A]"
                      }`}
                    >
                      <span className="text-xs font-bold block">{r.label}</span>
                      <span className="text-[10px] text-[#404040]">{r.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit" disabled={loading}
              className="w-full py-3 bg-[#002FA7] text-white text-sm font-bold rounded-sm hover:bg-[#001D6C] transition-colors disabled:opacity-50"
            >
              {loading ? "Please wait..." : isRegister ? "Create Account" : "Sign In"}
            </button>

            <p className="text-[9px] text-[#404040] mt-3 leading-relaxed text-center">
              By signing in you agree to our Terms of Use. All tools, calculators, AI inspections, 
              and EMF readings provided by WorkPro are for <b>informational reference only</b> and 
              do not replace professional inspections, certified testing equipment, or licensed 
              engineering. WorkPro assumes no liability for injury, property damage, code violations, 
              or financial loss resulting from use of this application.
            </p>
          </form>

          <div className="mt-4 text-center">
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setIsRegister(!isRegister); setError(""); }}
              className="text-xs text-[#002FA7] hover:underline"
            >
              {isRegister ? "Already have an account? Sign in" : "Need an account? Register"}
            </button>
          </div>
        </div>

        {/* Demo Credentials - removed for security */}
      </div>
    </div>
  );
}
